import {Box, Card, useTheme} from '@mui/material'
import type {
  GridCellParams,
  GridColDef,
  GridEventListener,
  GridFilterModel,
  GridLocaleText,
  GridRowClassNameParams,
  GridRowHeightParams,
  GridRowId,
  GridRowModel,
  GridRowParams,
  GridRowSelectionModel,
  GridSortModel,
} from '@mui/x-data-grid'
import {DataGrid} from '@mui/x-data-grid'
import type {ReactElement} from 'react'
import {useEffect, useRef} from 'react'
import {useSearchParams} from 'react-router-dom'
import {useCustomTranslation} from '@hooks/useCustomTranslation'
import useDataGridSelectAll from '@hooks/useDataGridSelectAll'
import {useIsReadOnly} from '@hooks/useIsReadOnly'
import {isForbiddenError} from '@shared/utils/apiErrors'
import DataGridError from '@shared/components/DataGridError'
import ErrorBoundary from '@shared/components/ErrorBoundary'
import SimpleSearchWithSearchParams from '@shared/components/SimpleSearchWithSearchParams'
import {tables} from '@shared/constants/lineManagement'
import {STANDARD_PAGE_SIZE_OPTIONS} from '@shared/constants/preferences/pagination'
import {useDataGridColumnWidths} from './useDataGridColumnWidths'

const NoRowsOverlay = ({message}: {message: string}): ReactElement => (
  <DataGridError message={message} />
)

interface ReusableDataGridProps {
  rows: GridRowModel[]
  columns: GridColDef[]
  loading: boolean
  /**
   * Did the query fail? Optional when `queryError` is given — an RTK error
   * object only exists when the query failed, so passing both is redundant and
   * passing only `error` is the half-wiring that makes a 403 read as an outage.
   */
  error?: boolean
  rowHeight: number
  onRowSelectionModelChange?: (newSelection: GridRowSelectionModel) => void
  processRowUpdate?: (
    newRow: GridRowModel,
    oldRow: GridRowModel,
  ) => GridRowModel | Promise<GridRowModel>
  noRowsMessage: string
  /**
   * Shown instead of `noRowsMessage` when `error` is set.
   *
   * Optional because most callers want the generic sentence; give one where
   * the grid can say something more useful about what failed.
   */
  errorMessage?: string
  /**
   * RTK Query's `error`. A 403 swaps the empty-state text for a "you lack the
   * permission" message; every other error is left to the caller's `error` flag.
   */
  queryError?: unknown
  height: number | string
  paginationModel: {page: number; pageSize: number}
  onPaginationModelChange: (newPagination: {page: number; pageSize: number}) => void
  keepNonExistentRowsSelected?: boolean
  isCellEditable?: (params: GridCellParams) => boolean
  title: string
  addItemButton?: ReactElement | undefined
  rowSelectionModel?: GridRowSelectionModel | undefined
  onSortModelChange: (sortModel: GridSortModel) => void
  sortModel?: GridSortModel
  /**
   * Server-side column filtering. Passing `onFilterModelChange` turns the
   * column filter menu on (filterMode="server"); without it the filter stays
   * disabled, as for every grid that has no backend filter to map it to.
   */
  filterModel?: GridFilterModel
  onFilterModelChange?: (model: GridFilterModel) => void
  customRightHeaderActions?: ReactElement | undefined
  customLeftHeaderActions?: ReactElement | undefined
  hideHeader?: boolean
  rowCount?: number
  pageSizeOptions?: number[]
  titleSearchParams: string
  /**
   * Extra URL params that narrow the same list. A selection carried across one
   * of these would be a bulk action on rows the user can no longer see, so a
   * change to any of them clears it — the search box is just the one every
   * grid has.
   */
  selectionResetParams?: string[]
  checkboxSelection?: boolean
  disableRowSelectionOnClick?: boolean
  isRowSelectable?: (params: GridRowParams) => boolean
  hideSearch?: boolean
  onRowClick?: GridEventListener<'rowClick'>
  localeText?: Partial<GridLocaleText>
  fetchAllIds?: () => Promise<GridRowId[]>
  getRowHeight?: (params: GridRowHeightParams) => number | null | 'auto'
  getRowClassName?: (params: GridRowClassNameParams) => string
  hideCellFocusOutline?: boolean
  autoHeight?: boolean
  'data-testid'?: string
  'data-cy'?: string
  className?: string
  sx?: object
  /**
   * Stable key under which user-resized column widths are persisted (state +
   * localStorage) so they survive re-renders/reloads. Defaults to `title`.
   * Pass an explicit value when two grids share a title but have different columns.
   */
  columnWidthsKey?: string
}

const BasicDataGrid = ({
  rows,
  columns,
  loading = false,
  // No default: `failed` below distinguishes "not given" from "given false".
  error,
  rowHeight,
  onRowSelectionModelChange,
  processRowUpdate,
  noRowsMessage = 'No rows available',
  errorMessage,
  queryError,
  height,
  paginationModel,
  onPaginationModelChange,
  keepNonExistentRowsSelected = false,
  isCellEditable,
  title,
  addItemButton,
  rowSelectionModel,
  onSortModelChange,
  sortModel,
  filterModel,
  onFilterModelChange,
  customRightHeaderActions,
  customLeftHeaderActions,
  hideHeader = false,
  rowCount,
  pageSizeOptions,
  titleSearchParams,
  selectionResetParams = [],
  checkboxSelection,
  disableRowSelectionOnClick,
  isRowSelectable,
  hideSearch = false,
  onRowClick,
  localeText,
  fetchAllIds,
  getRowHeight,
  getRowClassName,
  hideCellFocusOutline = false,
  autoHeight = false,
  'data-testid': dataTestId,
  'data-cy': dataCy,
  className,
  sx: extraSx,
  columnWidthsKey,
}: ReusableDataGridProps): ReactElement => {
  const theme = useTheme()
  const isReadOnly = useIsReadOnly()
  const [t] = useCustomTranslation('common')
  // `titleSearchParams` over `title`: it's a required prop on every caller (title can be ""),
  // and already has to be app-wide-unique by construction -- it's a URL query-param name, so two
  // visible grids sharing one would already break each other's search box. `title` is only the
  // last-resort fallback for the rare caller with no search param (e.g. `hideSearch` grids).
  const {columns: persistedColumns, onColumnWidthChange} = useDataGridColumnWidths(
    columnWidthsKey || titleSearchParams || title,
    columns,
  )

  const {handleRowSelectionModelChange, selectAllControl} = useDataGridSelectAll({
    rows,
    rowCount: rowCount ?? 0,
    rowSelectionModel,
    onRowSelectionModelChange,
    fetchAllIds,
    enabled: Boolean(checkboxSelection),
  })

  // Selections survive paging (keepNonExistentRowsSelected), so a stale selection could
  // silently include rows a new filter left out — reset it when the filter changes.
  //
  // Not just the search box: any URL-backed filter narrows the same list, and a
  // selection carried across one of those is a bulk action on rows the user can
  // no longer see. Callers name their own filter params.
  const [searchParams] = useSearchParams()
  const filterSignature = [titleSearchParams, ...selectionResetParams]
    .filter((param): param is string => Boolean(param))
    .map((param) => `${param}=${searchParams.get(param) ?? ''}`)
    .join('&')
  const previousSearchRef = useRef(filterSignature)
  useEffect(() => {
    if (previousSearchRef.current === filterSignature) {
      return
    }
    previousSearchRef.current = filterSignature
    if (
      rowSelectionModel &&
      (rowSelectionModel.type === 'exclude' || rowSelectionModel.ids.size > 0)
    ) {
      onRowSelectionModelChange?.({type: 'include', ids: new Set([])})
    }
  }, [filterSignature, rowSelectionModel, onRowSelectionModelChange])

  // Read-only platform users may view but not edit: strip inline-edit affordances
  // (editable columns + row-update handler) so cells render but cannot be changed. Applied on
  // top of the width-persisted columns (AB#5380) so a read-only user's grid still keeps
  // everyone's saved widths, just without the edit affordances.
  const effectiveColumns = isReadOnly
    ? persistedColumns.map((column) => ({...column, editable: false}))
    : persistedColumns
  const effectiveProcessRowUpdate = isReadOnly ? undefined : processRowUpdate
  const effectiveIsCellEditable = isReadOnly ? () => false : isCellEditable

  const rightHeaderActions = (): ReactElement => (
    <Box
      sx={{
        justifyContent: 'flex-end',
        display: 'flex',
        gap: 2,
        // Wrap instead of overflowing on narrow screens; ml:auto keeps the group
        // right-aligned when it wraps onto its own line.
        flexWrap: 'wrap',
        rowGap: 1,
        ml: 'auto',
        minWidth: 0,
      }}
    >
      {customRightHeaderActions}
      {addItemButton}
    </Box>
  )

  const leftHeaderActions = (): ReactElement => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-start',
        alignItems: 'stretch',
        gap: 2,
        // Not height: '100%': once the header wraps, the percentage resolves against the
        // header's own height and the wrapped line spills under the grid. alignSelf gives
        // the same single-line stretch without that.
        alignSelf: 'stretch',
        flexWrap: 'wrap',
        rowGap: 1,
        minWidth: 0,
      }}
    >
      {!hideSearch && <SimpleSearchWithSearchParams title={titleSearchParams} />}
      {selectAllControl}
      {customLeftHeaderActions}
    </Box>
  )

  // `queryError` alone is enough. Three grids passed `error` without it, so a
  // 403 rendered as "something went wrong" and the person who could fix it —
  // an admin granting a role — had nothing on screen to tell them so
  // (AB#5869). Deriving it here means a caller cannot wire this half-way.
  const failed = error ?? queryError !== undefined
  const forbidden = isForbiddenError(queryError)
  /*
   * Three different situations, three different sentences.
   *
   * They used to share one. The overlay was installed only when something had
   * gone wrong, and the text it showed was `noRowsMessage` — so a 500 read
   * "No reports yet", which does not merely confuse: it MASKS an outage, and
   * generates bug reports about data disappearing. Meanwhile a genuinely empty
   * grid fell through to MUI's own untranslated "No rows", so the sentence the
   * caller wrote for exactly that case was reachable only on failure.
   */
  const overlayMessage: string = forbidden
    ? String(t('errors.missing_permission'))
    : failed
      ? (errorMessage ?? String(t('errors.something_went_wrong')))
      : noRowsMessage
  const noRowsOverlayComponent = (): ReactElement => <NoRowsOverlay message={overlayMessage} />
  // Fill mode: caller's ancestor is a real flex/height-bound layout (see BasicDashboardContainer),
  // so the grid can size itself from that instead of a hand-computed calc(100vh - Npx).
  const isFillHeight = !autoHeight && height === '100%'
  const resolvedHeight = autoHeight
    ? undefined
    : typeof height === 'number'
      ? `calc(100vh - ${height}px)`
      : height
  const resolvedPageSizeOptions = pageSizeOptions ?? STANDARD_PAGE_SIZE_OPTIONS

  return (
    <ErrorBoundary fallbackComponent={<DataGridError message={overlayMessage} />}>
      <Box
        sx={
          isFillHeight
            ? {height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column'}
            : undefined
        }
      >
        {!hideHeader ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              // Toolbars wrap rather than overflow (and get clipped) on narrow screens.
              flexWrap: 'wrap',
              columnGap: 2,
              rowGap: 1,
              mb: 2,
              ...(isFillHeight ? {flex: '0 0 auto'} : {}),
            }}
            data-testid="basic-data-grid"
            data-cy={`basic-data-grid-${title}`}
          >
            {leftHeaderActions()}
            {rightHeaderActions()}
          </Box>
        ) : null}
        <Card
          data-testid={dataTestId}
          data-cy={dataCy}
          sx={
            isFillHeight
              ? {flex: '1 1 0%', minHeight: 0, display: 'flex', flexDirection: 'column'}
              : undefined
          }
        >
          <DataGrid
            rows={rows}
            columns={effectiveColumns}
            onColumnWidthChange={onColumnWidthChange}
            rowHeight={rowHeight || tables.rowHeight}
            loading={loading}
            rowCount={rowCount}
            paginationMode="server"
            keepNonExistentRowsSelected={keepNonExistentRowsSelected}
            rowSelectionModel={rowSelectionModel}
            isCellEditable={effectiveIsCellEditable}
            // Always ours, not only on failure: otherwise an empty grid shows
            // MUI's untranslated "No rows" and the caller's own sentence is
            // reachable only when something has gone wrong.
            slots={{noRowsOverlay: noRowsOverlayComponent}}
            onRowSelectionModelChange={handleRowSelectionModelChange}
            processRowUpdate={effectiveProcessRowUpdate}
            paginationModel={paginationModel}
            onPaginationModelChange={onPaginationModelChange}
            sortingMode="server"
            disableColumnFilter={!onFilterModelChange}
            {...(onFilterModelChange
              ? {filterMode: 'server' as const, filterModel, onFilterModelChange}
              : {})}
            sortModel={sortModel}
            onSortModelChange={onSortModelChange}
            pageSizeOptions={resolvedPageSizeOptions}
            checkboxSelection={checkboxSelection}
            disableRowSelectionOnClick={disableRowSelectionOnClick}
            isRowSelectable={isRowSelectable}
            onRowClick={onRowClick}
            localeText={localeText}
            getRowHeight={getRowHeight}
            getRowClassName={getRowClassName}
            autoHeight={autoHeight}
            className={className}
            sx={{
              border: 'none',
              height: resolvedHeight,
              ...(isFillHeight ? {flex: '1 1 0%', minHeight: 0} : {}),
              '--DataGrid-containerBackground': theme.palette.background.paper,
              /*
               * Suppresses the ring MUI draws after a MOUSE click, which is
               * what this flag was added for — and used to suppress the
               * keyboard one too, so a keyboard user moved through the grid
               * with nothing on screen telling them where they were.
               *
               * `:focus-visible` is exactly the distinction: the browser sets
               * it for keyboard focus and not for a click. Restored after the
               * blanket rule, so it wins.
               */
              ...(hideCellFocusOutline
                ? {
                    '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within':
                      {
                        outline: 'none',
                      },
                    '& .MuiDataGrid-columnHeader:focus-visible, & .MuiDataGrid-cell:focus-visible':
                      {
                        outline: `2px solid ${theme.palette.primary.main}`,
                        outlineOffset: -2,
                      },
                  }
                : {}),
              ...extraSx,
            }}
          />
        </Card>
      </Box>
    </ErrorBoundary>
  )
}

export default BasicDataGrid
